'use client'

import { useState, useEffect, useMemo, useRef } from 'react'
import { usePathname } from 'next/navigation'
import MainSearchBar from '@/components/search/MainSearchBar'
import { usePageUrls, useDefaultLanguage } from '@/contexts/PageUrlsContext'
import { useProductCategories } from '@/contexts/ProductCategoriesContext'
import { getProductFilters } from '@/lib/api/product-search'
import type { SearchField } from '@/types/product-search'
import type { CategoryTabItem } from '@/components/search/MainSearchBar'
import type { PageUrlMap } from '@/types/blog'
import { mainSearchApiProductType } from '@/lib/main-search'
import { resolveSearchTabListingType } from '@/lib/url-helpers'

function toCategoryTabItem(c: { id: number; name: string; slug: string; type?: string; search_tab_title?: string; search_tab_icon?: string; tabTitle?: string; tabIconUrl?: string }): CategoryTabItem {
    const listingType = resolveSearchTabListingType({ type: c.type, slug: c.slug })
    return {
        id: c.id,
        name: c.name,
        slug: c.slug,
        type: listingType ?? c.type ?? 'tour',
        tabTitle: c.search_tab_title ?? c.tabTitle ?? undefined,
        tabIconUrl: c.search_tab_icon ?? c.tabIconUrl ?? undefined,
    }
}

function mergePageUrlMaps(context?: PageUrlMap, ssr?: PageUrlMap): PageUrlMap | undefined {
    const merged: PageUrlMap = { ...(context ?? {}), ...(ssr ?? {}) }
    return Object.keys(merged).length > 0 ? merged : undefined
}

export interface HomeHeaderSearchBarProps {
    activeTab?: number
    /** SSR: sayfa render sırasında çekilen sekmeler (client fetch gecikmesini önler) */
    categoryTabs?: CategoryTabItem[]
    selectedCategoryId?: number
    searchFields?: SearchField[]
    language?: string
    defaultLanguage?: string
    pageUrls?: Record<string, string>
    multiTab?: boolean
}

/**
 * Ana sayfa hero arama çubuğu.
 * Sekmeler: layout + sayfa SSR; yoksa context / API fallback.
 */
export default function HomeHeaderSearchBar({
    activeTab: _activeTab,
    categoryTabs: ssrCategoryTabs,
    selectedCategoryId: ssrSelectedCategoryId,
    searchFields: ssrSearchFields,
    language: ssrLanguage,
    defaultLanguage: ssrDefaultLanguage,
    pageUrls: ssrPageUrls,
    multiTab: ssrMultiTab,
}: HomeHeaderSearchBarProps) {
    const pathname = usePathname()
    const contextPageUrls = usePageUrls()
    const layoutDefaultLanguage = useDefaultLanguage()
    const contextCategories = useProductCategories()

    const pageUrls = useMemo(
        () => mergePageUrlMaps(contextPageUrls, ssrPageUrls),
        [contextPageUrls, ssrPageUrls],
    )
    const defaultLanguage = ssrDefaultLanguage ?? layoutDefaultLanguage ?? 'en'

    const initialTabs = useMemo(() => {
        if (ssrCategoryTabs && ssrCategoryTabs.length > 0) {
            return ssrCategoryTabs.map(toCategoryTabItem)
        }
        if (contextCategories.length > 0) {
            return contextCategories.map(toCategoryTabItem)
        }
        return []
    }, [ssrCategoryTabs, contextCategories])

    const [categoryTabs, setCategoryTabs] = useState<CategoryTabItem[]>(initialTabs)
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
        ssrSelectedCategoryId ?? (initialTabs[0]?.id ?? null),
    )
    const [searchFields, setSearchFields] = useState<SearchField[]>(ssrSearchFields ?? [])
    const [isFiltersLoading, setIsFiltersLoading] = useState(
        !(ssrSearchFields && ssrSearchFields.length > 0) && initialTabs.length > 0,
    )
    const filtersLoadedKeyRef = useRef<string | null>(
        ssrSearchFields && ssrSearchFields.length > 0 && initialTabs.length > 0
            ? `${initialTabs[0]?.type ?? 'tour'}|${ssrLanguage ?? 'en'}`
            : null,
    )

    const resolvedLanguage = useMemo(() => {
        if (ssrLanguage) return ssrLanguage
        if (typeof window === 'undefined' || !pathname) return 'en'
        const m = pathname.match(/^\/([a-z]{2})\//)
        return m ? m[1] : (pathname.startsWith('/tr') ? 'tr' : 'en')
    }, [pathname, ssrLanguage])

    useEffect(() => {
        if (ssrCategoryTabs && ssrCategoryTabs.length > 0) {
            setCategoryTabs(ssrCategoryTabs.map(toCategoryTabItem))
            return
        }
        if (contextCategories.length > 0) {
            const list = contextCategories.map(toCategoryTabItem)
            setCategoryTabs(list)
            setSelectedCategoryId((prev) => (prev == null && list.length > 0 ? list[0].id : prev))
            return
        }
        let cancelled = false
        const load = async () => {
            try {
                const params = new URLSearchParams()
                params.set('language', resolvedLanguage)
                params.set('is_active', '1')
                params.set('show_in_search_tabs', '1')
                const res = await fetch(`/api/product-categories?${params.toString()}`)
                if (!res.ok || cancelled) return
                const data = await res.json()
                const list = (data?.data ?? []).map((c: { id: number; name: string; slug: string; type?: string; search_tab_title?: string; search_tab_icon?: string }) => toCategoryTabItem(c))
                if (!cancelled) {
                    setCategoryTabs(list)
                    if (list.length > 0) setSelectedCategoryId((prev) => (prev == null ? list[0].id : prev))
                }
            } catch {
                if (!cancelled) setCategoryTabs([])
            }
        }
        load()
        return () => { cancelled = true }
    }, [resolvedLanguage, contextCategories, ssrCategoryTabs])

    useEffect(() => {
        if (ssrSearchFields && ssrSearchFields.length > 0) {
            setSearchFields(ssrSearchFields)
            setIsFiltersLoading(false)
        }
    }, [ssrSearchFields])

    const selectedCategory = useMemo(() => categoryTabs.find((c) => c.id === selectedCategoryId) ?? categoryTabs[0], [categoryTabs, selectedCategoryId])
    const productTypeForFilters = useMemo(
        () => mainSearchApiProductType(selectedCategory ?? null, 'tour'),
        [selectedCategory],
    )

    useEffect(() => {
        if (ssrSearchFields && ssrSearchFields.length > 0) {
            return
        }
        if (!productTypeForFilters || categoryTabs.length === 0) {
            setIsFiltersLoading(false)
            return
        }
        const key = `${productTypeForFilters}|${resolvedLanguage}`
        if (filtersLoadedKeyRef.current === key) {
            setIsFiltersLoading(false)
            return
        }
        let cancelled = false
        setIsFiltersLoading(true)
        getProductFilters(productTypeForFilters, resolvedLanguage)
            .then((config) => {
                if (!cancelled) {
                    filtersLoadedKeyRef.current = key
                    setSearchFields(config.search_fields ?? [])
                }
            })
            .catch(() => {
                if (!cancelled) setSearchFields([])
            })
            .finally(() => {
                if (!cancelled) setIsFiltersLoading(false)
            })
        return () => { cancelled = true }
    }, [productTypeForFilters, resolvedLanguage, categoryTabs.length, ssrSearchFields])

    if (categoryTabs.length === 0 && !isFiltersLoading) {
        return null
    }

    return (
        <div className="relative z-40 pointer-events-none">
            <div className="pointer-events-auto">
            <MainSearchBar
                productType={productTypeForFilters}
                searchFields={searchFields}
                language={resolvedLanguage}
                defaultLanguage={defaultLanguage}
                pageUrls={pageUrls}
                categoryTabs={categoryTabs}
                selectedCategoryId={selectedCategoryId}
                onCategoryTabChange={(c) => setSelectedCategoryId(c?.id ?? null)}
                isLoading={isFiltersLoading && searchFields.length === 0}
                multiTab={ssrMultiTab ?? categoryTabs.length > 1}
            />
            </div>
        </div>
    )
}
